import { match } from 'ts-pattern'
import { toCurrencyName } from '@sequencehq/utils'
import type { Currency } from '@sequencehq/api/commonEnums'
import {
  type ActivityLogDiffFn,
  type DiffResult,
  type ParsedJavaObject,
  type ParsedJavaValue,
  defaultDiffFn,
  getFieldLabel
} from 'ActivityLogs/ActivityLogItem/activityLogDiff.helpers'
import { accountDetailsTypeDescription } from './bankDetails.types'

/**
 * Domain-specific diff function for the bank details activity log.
 * Groups bankAccountDetails changes by (currency key, BankPaymentDestinations type)
 * and determines a title based on the overall operation (added, removed, or updated).
 * All other fields delegate to the default diff function.
 */
export const paymentDetailsDiffFn: ActivityLogDiffFn = change => {
  if (change.field === 'bankAccountDetails') {
    return diffBankAccountDetails(change.previousValue, change.newValue)
  }
  return defaultDiffFn(change)
}

// --- Label formatting ---

const formatCurrencyLabel = (code: string): string => {
  const name = toCurrencyName(code as Currency)
  return name !== code ? `${code} - ${name}` : code
}

const formatAccountTypeLabel = (type: string): string =>
  (accountDetailsTypeDescription as Record<string, string>)[type] ?? type

// --- Bank account details diffing ---

type KeyedEntry = {
  compositeKey: string
  currency: string
  type: string
  leaves: Map<string, string>
}

const diffBankAccountDetails = (
  previousValue: ParsedJavaValue,
  newValue: ParsedJavaValue
): DiffResult => {
  const oldEntries = extractKeyedEntries(previousValue)
  const newEntries = extractKeyedEntries(newValue)

  const oldByKey = new Map(oldEntries.map(e => [e.compositeKey, e.leaves]))
  const newByKey = new Map(newEntries.map(e => [e.compositeKey, e.leaves]))

  const addedLines = newEntries.reduce<string[]>((acc, entry) => {
    if (oldByKey.has(entry.compositeKey)) return acc
    const detailLines = [...entry.leaves.entries()].reduce<string[]>(
      (lineAcc, [field, val]) =>
        field === 'type'
          ? lineAcc
          : [...lineAcc, `${getFieldLabel(field)} set to ${val}`],
      []
    )
    return [
      ...acc,
      `Currency ${formatCurrencyLabel(entry.currency)}`,
      `Account type ${formatAccountTypeLabel(entry.type)}`,
      ...detailLines
    ]
  }, [])

  const updatedLines = newEntries.reduce<string[]>((acc, entry) => {
    const oldLeaves = oldByKey.get(entry.compositeKey)
    if (!oldLeaves) return acc
    const detailLines = diffLeaves(oldLeaves, entry.leaves)
    return detailLines.length > 0
      ? [
          ...acc,
          `Currency ${formatCurrencyLabel(entry.currency)}`,
          `Account type ${formatAccountTypeLabel(entry.type)}`,
          ...detailLines
        ]
      : acc
  }, [])

  const removedLines = oldEntries.reduce<string[]>((acc, entry) => {
    if (newByKey.has(entry.compositeKey)) return acc
    const detailLines = [...entry.leaves.entries()].reduce<string[]>(
      (lineAcc, [field, val]) =>
        field === 'type'
          ? lineAcc
          : [...lineAcc, `${getFieldLabel(field)} was ${val}`],
      []
    )
    return [
      ...acc,
      `Currency ${formatCurrencyLabel(entry.currency)}`,
      `Account type ${formatAccountTypeLabel(entry.type)}`,
      ...detailLines
    ]
  }, [])

  const hasAdded = addedLines.length > 0
  const hasRemoved = removedLines.length > 0
  const hasUpdated = updatedLines.length > 0

  const title = match({ hasAdded, hasRemoved, hasUpdated })
    .with(
      { hasAdded: true, hasRemoved: false, hasUpdated: false },
      () => 'Bank details added'
    )
    .with(
      { hasAdded: false, hasRemoved: true, hasUpdated: false },
      () => 'Bank details removed'
    )
    .otherwise(() => 'Bank details updated')

  return {
    title,
    lines: [...addedLines, ...updatedLines, ...removedLines]
  }
}

const diffLeaves = (
  oldLeaves: Map<string, string>,
  newLeaves: Map<string, string>
): string[] => {
  const changed = [...newLeaves.entries()].reduce<string[]>(
    (acc, [field, newVal]) => {
      const oldVal = oldLeaves.get(field) ?? ''
      if (oldVal === newVal) return acc
      const label = getFieldLabel(field)
      const line = oldVal
        ? `${label} changed to ${newVal}`
        : `${label} set to ${newVal}`
      return [...acc, line]
    },
    []
  )

  const removedFields = [...oldLeaves.entries()].reduce<string[]>(
    (acc, [field]) => {
      if (newLeaves.has(field)) return acc
      return [...acc, `${getFieldLabel(field)} removed`]
    },
    []
  )

  return [...changed, ...removedFields]
}

// --- Entry extraction ---

/**
 * Extracts keyed entries from a parsed bankAccountDetails value.
 * Each entry is identified by a composite key of (currency:type).
 * Leaf fields are flattened from the nested structure.
 */
const extractKeyedEntries = (value: ParsedJavaValue): KeyedEntry[] => {
  if (!Array.isArray(value)) return []

  return value.reduce<KeyedEntry[]>((acc, item) => {
    if (!isObject(item)) return acc
    const key = typeof item.key === 'string' ? item.key : null
    if (!key) return acc

    const leaves = new Map<string, string>()
    const innerValue = item.value
    if (isObject(innerValue)) {
      collectLeafStrings(innerValue, leaves)
    }

    const type = leaves.get('type') ?? 'unknown'
    return [
      ...acc,
      { compositeKey: `${key}:${type}`, currency: key, type, leaves }
    ]
  }, [])
}

const collectLeafStrings = (
  obj: ParsedJavaObject,
  result: Map<string, string>
): void => {
  Object.entries(obj).reduce<Map<string, string>>((acc, [key, val]) => {
    if (val === null) return acc
    if (typeof val === 'string') {
      acc.set(key, val)
      return acc
    }
    if (!Array.isArray(val)) {
      collectLeafStrings(val, acc)
    }
    return acc
  }, result)
}

const isObject = (value: ParsedJavaValue): value is ParsedJavaObject =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
